/**
 * The board's columns, as a thing a person arranges (schema v11/v12).
 *
 * Columns are born in two places — an agent's exit rules mention them, or
 * somebody adds one here — and this screen is where their *order* lives, plus
 * the two operations rules cannot express: rename and remove.
 *
 * Renaming is the delicate one. A column's name is written in three places
 * that must move together: the board list, every agent rule that mentions it,
 * and the cards currently sitting in it. The first two travel in one document
 * write; the cards are moved right after the new workflow is live, because
 * a card in a state the board no longer lists is simply invisible.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  BOARD_GIVENS,
  columnReferences,
  parseTeamDocument,
  serializeTeamDocument,
  writeBoardColumns,
} from '../../logic/team-editor.js';
import { mutationContext } from '../../client/data-source.js';
import { describeSaveFailure, useApplyTeamDocument } from '../apply-team.js';
import { useBoard, useTeamWorkflow } from '../queries.js';
import { useStore, useStoreState } from '../store-context.js';
import { QueryPhase, Section } from '../components/state.js';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Flash } from '@primer/react';

interface Row {
  /** The running name, or null for a column that exists only in this form. */
  readonly original: string | null;
  readonly name: string;
  /** Kept out of sight on the board until «show hidden» is ticked there. */
  readonly hidden: boolean;
}

type SaveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'applied'; readonly version: number; readonly movedCards: number }
  | { readonly kind: 'unchanged' }
  | { readonly kind: 'rejected'; readonly title: string; readonly errors: readonly string[] };

export function BoardSettingsScreen(): ReactNode {
  const teamId = useStoreState((state) => state.currentTeamId);
  return <BoardSettingsForTeam key={teamId} />;
}

function BoardSettingsForTeam(): ReactNode {
  const workflow = useTeamWorkflow();
  const board = useBoard();
  const store = useStore();
  const client = useQueryClient();
  const apply = useApplyTeamDocument();

  const [rows, setRows] = useState<readonly Row[] | null>(null);
  const [hiddenGivens, setHiddenGivens] = useState<ReadonlySet<string>>(new Set());
  const [addName, setAddName] = useState('');
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  const [references, setReferences] = useState<ReadonlyMap<string, readonly string[]>>(new Map());

  /*
   * Seeded from the running workflow — the truth of "which columns exist
   * today" — not from the document's list alone, which may name only some of
   * them. Re-seeded when the workflow changes underneath an untouched form.
   */
  useEffect(() => {
    if (rows !== null || workflow.data === undefined) return;
    const hiddenNow = new Set(workflow.data.states.filter((state) => state.hidden === true).map((state) => state.id));
    const middle = workflow.data.states
      .map((state) => state.id)
      .filter((id) => id !== BOARD_GIVENS.first && !BOARD_GIVENS.last.includes(id as never));
    setRows(middle.map((id) => ({ original: id, name: id, hidden: hiddenNow.has(id) })));
    setHiddenGivens(new Set(BOARD_GIVENS.last.filter((id) => hiddenNow.has(id))));
  }, [rows, workflow.data]);

  // Which agents pin each column, from the newest stored document.
  useEffect(() => {
    const teamId = store.currentTeamId;
    if (teamId === null) return;
    let cancelled = false;
    void (async () => {
      const versions = await store.data.teamVersions();
      const newest = versions[0];
      if (newest === undefined) return;
      const source = await store.data.teamVersionDocument(teamId, newest.version);
      if (source === null || cancelled) return;
      setReferences(columnReferences(parseTeamDocument(source)));
    })().catch(() => {
      // The screen still works; removal protection falls back to card counts.
    });
    return () => {
      cancelled = true;
    };
  }, [store]);

  const cardCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const card of board.data?.cards ?? []) {
      counts.set(card.workflowState, (counts.get(card.workflowState) ?? 0) + 1);
    }
    return counts;
  }, [board.data]);

  if (workflow.isPending || rows === null) {
    return <QueryPhase isPending error={workflow.error} source="workflow" rows={4} />;
  }
  if (workflow.error) {
    return <QueryPhase isPending={false} error={workflow.error} source="workflow" rows={4} />;
  }

  const move = (index: number, direction: -1 | 1): void => {
    const next = [...rows];
    const swap = index + direction;
    const a = next[index];
    const b = next[swap];
    if (a === undefined || b === undefined) return;
    next[index] = b;
    next[swap] = a;
    setRows(next);
  };

  const onSave = (): void => {
    setSave({ kind: 'saving' });
    const renames = new Map<string, string>();
    for (const row of rows) {
      const name = row.name.trim();
      if (row.original !== null && name.length > 0 && name !== row.original) renames.set(row.original, name);
    }
    const columns = rows.map((row) => row.name.trim()).filter((name) => name.length > 0);
    const hidden = [
      ...rows.filter((row) => row.hidden).map((row) => row.name.trim()),
      ...hiddenGivens,
    ].filter((name) => name.length > 0);

    apply((source) => serializeTeamDocument(writeBoardColumns(parseTeamDocument(source), columns, renames, hidden)))
      .then(async (outcome) => {
        if (outcome.kind === 'unchanged' && renames.size === 0) {
          setSave({ kind: 'unchanged' });
          return;
        }
        /*
         * The cards follow their column. Done *after* activation, because the
         * new name only becomes a legal state once the new workflow runs; a
         * card left behind under the old name would vanish from the board.
         */
        let moved = 0;
        if (renames.size > 0) {
          for (const card of board.data?.cards ?? []) {
            const to = renames.get(card.workflowState);
            if (to === undefined) continue;
            await store.data.editCard(card.externalItemId, { workflowState: to }, mutationContext(store.config.actorId));
            moved += 1;
          }
          await client.invalidateQueries({ queryKey: ['board'] });
        }
        setSave({ kind: 'applied', version: outcome.kind === 'applied' ? outcome.version : 0, movedCards: moved });
        setRows(null); // Re-seed from the workflow that is now running.
      })
      .catch((error: unknown) => {
        const described = describeSaveFailure(error);
        setSave({ kind: 'rejected', title: described.title, errors: described.lines });
      });
  };

  return (
    <div className="stack">
      <Section
        label="Columns"
        lede="Cards travel left to right. Backlog opens the board and the two closing columns end it; everything between is yours — rename, reorder, add. «Hidden» keeps a column off the board until «show hidden» is ticked there — handy for done. Saving applies immediately."
      >
        <ol className="column-editor" aria-label="Board columns, in order">
          <li className="column-row column-row-given">
            <span className="column-name">{BOARD_GIVENS.first}</span>
            <span className="hint">where work starts — always first</span>
          </li>

          {rows.map((row, index) => {
            const cards = row.original === null ? 0 : (cardCounts.get(row.original) ?? 0);
            const pinnedBy = row.original === null ? [] : (references.get(row.original) ?? []);
            const removable = cards === 0 && pinnedBy.length === 0;
            return (
              <li className="column-row" key={row.original ?? `new-${String(index)}`}>
                <input
                  className="column-name-input"
                  value={row.name}
                  aria-label={`column ${row.original ?? 'new'}`}
                  onChange={(event) =>
                    setRows(rows.map((entry, at) => (at === index ? { ...entry, name: event.target.value } : entry)))
                  }
                />
                <span className="column-facts">
                  {cards > 0 ? `${String(cards)} card${cards === 1 ? '' : 's'}` : ''}
                  {pinnedBy.length > 0 ? `${cards > 0 ? ' · ' : ''}used by ${pinnedBy.join(', ')}` : ''}
                </span>
                <label className="check column-hidden" title="The board keeps this column out of sight until «show hidden» is ticked there">
                  <input
                    type="checkbox"
                    checked={row.hidden}
                    onChange={(event) =>
                      setRows(rows.map((entry, at) => (at === index ? { ...entry, hidden: event.target.checked } : entry)))
                    }
                  />
                  hidden
                </label>
                <span className="column-controls">
                  <Button aria-label="move up" disabled={index === 0} onClick={() => move(index, -1)}>
                    ↑
                  </Button>
                  <Button
                    aria-label="move down"
                    disabled={index === rows.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    ↓
                  </Button>
                  <Button
                    aria-label={`remove ${row.name}`}
                    disabled={!removable}
                    title={
                      removable
                        ? 'remove this column'
                        : cards > 0
                          ? 'move its cards first'
                          : `agents still use it: ${pinnedBy.join(', ')}`
                    }
                    onClick={() => setRows(rows.filter((_, at) => at !== index))}
                  >
                    −
                  </Button>
                </span>
              </li>
            );
          })}

          {BOARD_GIVENS.last.map((id) => (
            <li className="column-row column-row-given" key={id}>
              <span className="column-name">{id}</span>
              <span className="hint">{id === 'done' ? 'completion accepted — always near the end' : 'waits for a person — always last'}</span>
              <label className="check column-hidden" title="The board keeps this column out of sight until «show hidden» is ticked there">
                <input
                  type="checkbox"
                  checked={hiddenGivens.has(id)}
                  onChange={(event) => {
                    const next = new Set(hiddenGivens);
                    if (event.target.checked) next.add(id);
                    else next.delete(id);
                    setHiddenGivens(next);
                  }}
                />
                hidden
              </label>
            </li>
          ))}
        </ol>

        <div className="column-add">
          <input
            value={addName}
            placeholder="new-column-name"
            aria-label="new column name"
            onChange={(event) => setAddName(event.target.value)}
          />
          <Button
            disabled={addName.trim().length === 0}
            onClick={() => {
              setRows([...rows, { original: null, name: addName.trim(), hidden: false }]);
              setAddName('');
            }}
          >
            + add a column
          </Button>
        </div>
        <p className="hint">Names are lowercase words with dashes: <code>in-review</code>, <code>icebox</code>. Renaming a column carries its cards and every agent rule with it.</p>
      </Section>

      <div className="save-bar">
        {save.kind === 'rejected' ? (
          <Flash variant="danger" role="alert">
            <strong>{save.title}</strong>
            <ul className="finding-list">
              {save.errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </Flash>
        ) : null}
        {save.kind === 'unchanged' ? <p className="hint">Nothing changed — the board already looks like this.</p> : null}
        {save.kind === 'applied' ? (
          <Flash variant="success">
            <strong>The board now runs this arrangement.</strong>
            {save.movedCards > 0 ? <p>{save.movedCards} card(s) followed their renamed column.</p> : null}
          </Flash>
        ) : null}
        <div className="save-row">
          <Button variant="primary" onClick={onSave} disabled={save.kind === 'saving'}>
            {save.kind === 'saving' ? 'Applying…' : 'Save and apply'}
          </Button>
          <p className="hint save-hint">Renaming a column carries its cards and every agent rule with it.</p>
        </div>
      </div>
    </div>
  );
}
